'use client'

/**
 * The send-feedback modal's footer and its header shortcut hint (#14531).
 *
 * Lifted out of `../feedback-modal.tsx`, which sits just under the 1000-line
 * source cap (`check:file-size-ratchet`); #14531 grew both regions, so they
 * move here rather than the modal crossing the cap. Both are presentational:
 * the reset and its Undo are owned by the modal, which is where the state
 * they touch lives.
 */

import { Button } from '@/components/ui/button'
import { Loader2, RotateCcw } from '@/components/icons'

const START_OVER_HINT_ID = 'feedback-start-over-hint'

/**
 * "⇧ F anywhere to trigger", as a subtext line under the header's title row.
 * Hidden on mobile by the caller, where there is no keyboard to press it on.
 */
export function FeedbackShortcutHint() {
  return (
    <p className="mt-[3px] text-xs text-text-secondary">
      <kbd className="px-1.5 py-0.5 bg-fill-10 rounded text-xs">⇧</kbd>
      <kbd className="px-1.5 py-0.5 bg-fill-10 rounded text-xs ml-0.5">F</kbd>
      {' anywhere to trigger'}
    </p>
  )
}

type FeedbackModalFooterProps = {
  /** A saved draft was restored on open; gates the Start over control. */
  showStartOver: boolean
  onStartOver: () => void
  /**
   * An upload, capture or submit is in flight. Clearing then would race it —
   * a capture or upload landing into the emptied form, or a submit of a form
   * the submitter just reset — so the control waits.
   */
  startOverDisabled: boolean
  onCancel: () => void
  onSubmit: () => void
  canSubmit: boolean
  isSubmitting: boolean
}

/**
 * Start over (left, only for a restored draft) · Cancel · Send Feedback.
 *
 * Start over is kept apart from Send on purpose: it resets the whole form,
 * so it is a form-level action, and a quiet ghost button far from the primary
 * one is hard to hit by accident. The subtext is wired as the button's
 * accessible description, so a screen reader hears why the control exists.
 */
export function FeedbackModalFooter({
  showStartOver,
  onStartOver,
  startOverDisabled,
  onCancel,
  onSubmit,
  canSubmit,
  isSubmitting,
}: FeedbackModalFooterProps) {
  return (
    <div className="flex w-full items-center justify-between gap-2">
      {showStartOver && (
        <div className="flex flex-col items-start">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onStartOver}
            disabled={startOverDisabled}
            aria-describedby={START_OVER_HINT_ID}
            data-testid="feedback-start-over-button"
            className="gap-1.5"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            Start over
          </Button>
          <span
            id={START_OVER_HINT_ID}
            className="px-3 text-xs text-text-secondary"
          >
            Restored from last time
          </span>
        </div>
      )}
      <div className="ml-auto flex gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="gap-2"
        >
          {isSubmitting && (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          )}
          {isSubmitting ? 'Sending...' : 'Send Feedback'}
        </Button>
      </div>
    </div>
  )
}
